/**
 * V112-3-10: the summary strip that opens both scope screens (design decision 1: "which answers does a system
 * override" in under five seconds). Each cell is a count, a state chip and the names it counts, and leads to them:
 * a link (another page), a button (an answer on this page) or plain with its own action.
 */
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';

export interface StripCell {
  key: string;
  /** Large first line: a count ("4", "8 of 8") or a system name. */
  top: ReactNode;
  chips?: ReactNode;
  names?: string;
  /** Accent fill (the system sets answers) or the to-answer fill. */
  tone?: 'hot' | 'todo';
  to?: string;
  onClick?: () => void;
  /** A primary action inside the cell (the cell itself is then not clickable). */
  action?: ReactNode;
  label?: string;
}

export function ScopeSummaryStrip({ cells, label }: { cells: StripCell[]; label: string }) {
  return (
    <div className="sc-card sc-strip" role="group" aria-label={label}>
      {cells.map((c) => {
        const inner = (
          <>
            <span className="sc-cnt">{c.top}</span>
            {c.chips}
            {c.names ? <span className="sc-names">{c.names}</span> : null}
            {c.action}
          </>
        );
        const common = { className: 'sc-cell', 'data-hot': c.tone === 'hot' || undefined, 'data-todo': c.tone === 'todo' || undefined };
        if (c.action) return <div key={c.key} {...common}>{inner}</div>;
        if (c.to) return <Link key={c.key} to={c.to} {...common} aria-label={c.label}>{inner}</Link>;
        if (c.onClick) return <button key={c.key} type="button" {...common} aria-label={c.label} onClick={c.onClick}>{inner}</button>;
        return <div key={c.key} {...common}>{inner}</div>;
      })}
    </div>
  );
}

/** Scroll an answer into view and focus its heading (or a control inside it). */
export function focusAnswer(answer: string, selector?: string) {
  const row = document.getElementById(`a-${answer}`);
  row?.scrollIntoView?.({ block: 'center' });
  const target = (selector ? row?.querySelector<HTMLElement>(selector) : null) ?? document.getElementById(`h-${answer}`);
  target?.focus();
}
